import { TransposeDemo } from "@web/src/components/landing/transpose-demo";
import { RealtimeDemo } from "@web/src/components/landing/realtime-demo";
import { ArrangementDemo } from "@web/src/components/landing/arrangement-demo";
import { MetronomeDemo } from "@web/src/components/landing/metronome-demo";
import { DronePadDemo } from "@web/src/components/landing/drone-demo";
import {
  EASE_OUT,
  fadeUp,
  fadeUpTransition,
} from "@web/src/components/landing/motion-tokens";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import Image from "next/image";
import { useRef } from "react";
import { analytics } from "@web/src/services/analytics";

function FeatureSection({
  title,
  subtitle,
  children,
  align = "left",
  index,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  align?: "left" | "right";
  index: number;
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "end 0.1"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
  });

  const demoY = useTransform(smoothProgress, [0, 1], [40, -15]);

  const copyVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.1, delayChildren: 0.05 },
    },
  };

  return (
    <section
      ref={ref}
      className="flex items-center py-10 md:py-14 px-6 md:px-12 lg:px-20"
    >
      <div
        className={`w-full max-w-6xl mx-auto flex flex-col ${
          align === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
        } gap-8 lg:gap-14 items-center`}
      >
        <motion.div
          className="flex-1 max-w-md"
          data-reveal
          variants={copyVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10% 0px" }}
        >
          <motion.span
            className="text-xs uppercase tracking-[0.2em] text-theme-blue font-semibold mb-2 block"
            variants={fadeUp}
            transition={fadeUpTransition(0)}
          >
            0{index + 1}
          </motion.span>
          <motion.h3
            className="text-2xl md:text-3xl font-bold text-glyphs-primary mb-3 leading-tight"
            variants={fadeUp}
            transition={fadeUpTransition(0)}
          >
            {title}
          </motion.h3>
          <motion.p
            className="text-sm md:text-base text-glyphs-secondary leading-relaxed"
            variants={fadeUp}
            transition={fadeUpTransition(0)}
          >
            {subtitle}
          </motion.p>
        </motion.div>

        <motion.div className="flex-1 w-full" style={{ y: demoY }}>
          {children}
        </motion.div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="bg-surface-primary h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      {/*
        Entrance animations start at opacity 0 and are played by framer-motion.
        Without JavaScript nothing ever plays them, so the copy would render
        invisible to crawlers that execute CSS but not JS. This reveals it.
      */}
      <noscript>
        <style>{`[data-reveal],[data-reveal] *{opacity:1!important;filter:none!important;transform:none!important}`}</style>
      </noscript>

      {/* Sticky nav */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-surface-primary/80 border-b border-border-primary/50 safe-area-top"
        data-reveal
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image
              src="/setlist-icon.png"
              alt="Setlist"
              width={28}
              height={28}
              className="rounded-lg"
            />
            <span className="font-semibold text-glyphs-primary text-sm">
              Setlist
            </span>
          </div>
          <motion.a
            href="/login"
            className="text-xs font-medium px-4 py-2 rounded-lg bg-surface-button-primary text-glyphs-inverse-primary"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => analytics.landingCtaClicked("nav")}
          >
            Sign up
          </motion.a>
        </div>
      </motion.nav>

      {/* Hero - compact */}
      <section className="pt-[calc(env(safe-area-inset-top,0px)+6rem)] pb-8 px-6 text-center">
        <motion.div
          className="max-w-3xl mx-auto"
          data-reveal
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.08, delayChildren: 0.1 },
            },
          }}
        >
          <motion.h1
            className="text-4xl md:text-5xl font-bold text-glyphs-primary mb-4 leading-[1.1]"
            variants={fadeUp}
            transition={fadeUpTransition(0)}
          >
            Chord sheets for
            <br />
            the whole band
          </motion.h1>
          <motion.p
            className="text-base md:text-lg text-glyphs-secondary max-w-lg mx-auto mb-6 leading-relaxed"
            variants={fadeUp}
            transition={fadeUpTransition(0)}
          >
            Realtime multiplayer. Nashville Number System. Instant transpose.
            Built for teams that play together.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-3 justify-center"
            variants={fadeUp}
            transition={fadeUpTransition(0)}
          >
            <motion.a
              href="/login"
              className="px-6 py-2.5 rounded-lg font-medium text-sm bg-theme-blue text-white"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => analytics.landingCtaClicked("hero")}
            >
              Start for free
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" aria-labelledby="features-heading">
        <header className="px-6 pt-6 text-center">
          <h2
            id="features-heading"
            className="text-2xl md:text-3xl font-bold text-glyphs-primary mb-3 leading-tight"
          >
            Everything the band needs on one page
          </h2>
          <p className="text-sm md:text-base text-glyphs-secondary max-w-lg mx-auto leading-relaxed">
            Setlist keeps the chart, the key, the arrangement and the tempo
            together, so rehearsal time goes into playing rather than into
            hunting for the right version of a song.
          </p>
        </header>

        <FeatureSection
          index={0}
          title="Nashville Numbers & Transpose"
          subtitle="The fastest way to key in chord progressions. Toggle between numbers and chord letters instantly. View or hide lyrics with one tap. Transposing is just changing the key."
          align="left"
        >
          <TransposeDemo />
        </FeatureSection>

        <FeatureSection
          index={1}
          title="Multiplayer Chord Sheets"
          subtitle="Your Google Docs for chord sheets. Everyone views the same document and receives updates in realtime. Share a link — no download needed. Works offline and syncs when you're back."
          align="right"
        >
          <RealtimeDemo />
        </FeatureSection>

        <FeatureSection
          index={2}
          title="Prep Your Arrangements"
          subtitle="No more getting lost mid-song. Add arrangement sections with colour coding to remind you of every riff and note you need to hit."
          align="left"
        >
          <ArrangementDemo />
        </FeatureSection>

        <FeatureSection
          index={3}
          title="Built-in Metronome"
          subtitle="Every song has its tempo ready to go. No more searching up BPMs one by one — the whole team benefits from the prep."
          align="right"
        >
          <MetronomeDemo />
        </FeatureSection>

        <FeatureSection
          index={4}
          title="Drone Pad for Transitions"
          subtitle="An in-built drone pad for when you don't have a second keys player. Changing songs smoothly crossfades key for you — keeping the band supported."
          align="left"
        >
          <DronePadDemo />
        </FeatureSection>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 text-center">
        <motion.div
          data-reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE_OUT }}
          className="max-w-md mx-auto"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-glyphs-primary mb-3">
            Ready to play?
          </h2>
          <p className="text-sm text-glyphs-secondary mb-5">
            Free to use. Share with your whole band.
          </p>
          <motion.a
            href="/login"
            className="inline-block px-8 py-3 rounded-lg font-medium bg-theme-blue text-white text-sm"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => analytics.landingCtaClicked("bottom")}
          >
            Get started — it&apos;s free
          </motion.a>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border-primary py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Image
              src="/setlist-icon.png"
              alt="Setlist"
              width={20}
              height={20}
              className="rounded"
            />
            <span className="text-xs text-glyphs-secondary">
              Built for musicians, by musicians.
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="/privacy"
              className="text-xs text-glyphs-secondary hover:text-glyphs-primary transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              className="text-xs text-glyphs-secondary hover:text-glyphs-primary transition-colors"
            >
              Terms & Conditions
            </a>
            <a
              href="https://instagram.com/moosedevapps"
              target="_blank"
              rel="noopener noreferrer"
              className="text-glyphs-secondary hover:text-glyphs-primary transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
